import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex } from '@noble/hashes/utils.js';

/**
 * customer-app · 配置中枢（门店 / 网络 / 系统 / UI 语言）
 *
 * 来源：js/config-lib.js（已迁移）。
 *
 * 职责：统一 localStorage 与跨标签 storage 同步，提供 read/write 与 normalize 工具。
 *   须在 app.js 之前加载（顶层注册的 'storage' 监听器要早于业务）。
 *
 * 兼容契约（必须保留，被 app.js / admin-app.js / print-lib.js / customer-lite.js / admin.html 调用）：
 *   - `window.RestaurantConfigLib`：多项 export（KEYS、常量、默认对象、hash、R/W、normalizeKitchenCategoryPrintMode 等）
 *   - `OS.registerModule('configLib', …)`
 *   - 顶层副作用：监听 'storage'，跨标签转发 'restaurantInfoUpdated' / 'restaurantSystemSettingsUpdated' CustomEvent
 *   - saveXxx 写入后派发同名 CustomEvent，detail = 完整对象
 *
 * 设计：
 *   - 联合字面量类型（UiLangCode / CustomerBackgroundPreset / LockMinutesBetweenOrders / MealOverride）
 *     → 调用方拿到 SystemSettings 后可直接 narrow，无须自己再断言
 *   - 与 customer-bootstrap/types.ts 的 STORAGE_KEYS / BgPresetName 概念重叠，但因为分属
 *     不同 bundle（preflight / customer-app）无法跨包 import，故各自维护副本
 *   - SystemSettings 含厨房分类出单方式 kitchenCategoryPrintMode；admin 端可继续 spread 自有扩展键
 */

/* ─────────── 常量 + 类型字面量 ─────────── */

export const KEYS = {
  NETWORK: 'restaurantNetworkConfig',
  RESTAURANT_INFO: 'restaurantInfo',
  SYSTEM_SETTINGS: 'restaurantSystemSettings',
  UI_LANG: 'restaurantUiLang',
} as const;

export type StorageKey = (typeof KEYS)[keyof typeof KEYS];

/** 点餐/系统可选界面语言：与 LANG_PICKER 顺序一致（含法语） */
export const UI_LANG_ALLOWED = ['de', 'en', 'zh', 'es', 'fr', 'it', 'ja', 'tr'] as const;
export type UiLangCode = (typeof UI_LANG_ALLOWED)[number];

/** 顾客发单后距下一轮可点单的间隔（分钟）；仅 0（关闭）或 10 / 15 / 20 */
export const ORDER_LOCK_MINUTES_STOPS = [0, 10, 15, 20] as const;
export type LockMinutesBetweenOrders = (typeof ORDER_LOCK_MINUTES_STOPS)[number];

/** 顾客端页面背景预设（管理端「系统设置」） */
export const CUSTOMER_BACKGROUND_PRESET_ALLOWED = [
  'white',
  'black',
  'gray',
  'sky_blue',
  'lake_green',
  'light_purple',
  'china_red',
] as const;
export type CustomerBackgroundPreset = (typeof CUSTOMER_BACKGROUND_PRESET_ALLOWED)[number];

/** 旧值 → 新值映射（兼容历史已存值） */
const LEGACY_CUSTOMER_BACKGROUND_PRESET: Record<string, CustomerBackgroundPreset> = {
  light_green: 'lake_green',
  light_blue: 'sky_blue',
  light_gray: 'gray',
  light_red: 'china_red',
};

export type MealOverride = '' | 'lunch' | 'dinner' | 'special';

/** 与 printers.config.json `_categoryPrintMode` 一致：summary=总汇、single=一餐一单（按分类 id） */
export type KitchenCategoryPrintModeMap = Record<string, 'single' | 'summary'>;

/* ─────────── 数据形状 ─────────── */

export interface NetworkConfig {
  apiBaseUrl: string;
  printProxyUrl: string;
  /** 允许扩展（admin 可附加打印机别名映射等） */
  [key: string]: unknown;
}

export interface RestaurantInfo {
  logoUrl: string;
  name: string;
  street: string;
  zip: string;
  city: string;
  phone: string;
  email: string;
  website: string;
  [key: string]: unknown;
}

export interface SystemSettings {
  /** 每位客人每一轮（桌单发送前）购物车最多份数；0=不限制 */
  maxItemsPerOrder: number;
  /** 顾客发单后到下一轮可点单的间隔；仅 0/10/15/20 */
  lockMinutesBetweenOrders: LockMinutesBetweenOrders;
  menuLunchIds: string[];
  menuDinnerIds: string[];
  menuSpecialIds: string[];
  /** 顾客端未单独选择语言时沿用此项 */
  defaultUiLang: UiLangCode;
  /** 非空时顾客端固定该点餐时段（隐藏时段切换） */
  customerMealOverride: MealOverride;
  /** 顾客端登录页/点餐页外层背景；缺省或非法值时为 'black' */
  customerBackgroundPreset: CustomerBackgroundPreset;
  /** 管理后台是否要求登录（localStorage 仅存哈希，会话见 sessionStorage） */
  adminAuthEnabled: boolean;
  adminAuthUsername: string;
  /** SHA-256 hex（64 位 0-9a-f）；空字符串表示未设置 */
  adminAuthPasswordHash: string;
  /** Lite 顾客端登录页 Zum Menü 的密码；明文存储仅作店内物理闸门用 */
  customerLiteLoginPassword: string;
  /** 门店系统设定：厨房按分类出单方式；与 adminThermalSettings 合并时此项覆盖同键 */
  kitchenCategoryPrintMode: KitchenCategoryPrintModeMap;
}

export const DEFAULT_RESTAURANT_INFO: RestaurantInfo = {
  logoUrl: '',
  name: 'YUOKI GmbH',
  street: '',
  zip: '',
  city: '',
  phone: '',
  email: '',
  website: '',
};

/** SHA-256 hex of ordersystem-admin:Admin:admin123（管理后台默认口令，勿改算法前缀除非迁移数据） */
export const DEFAULT_ADMIN_AUTH_PASSWORD_HASH =
  'a5739324b6f2f114fd28f48b08282e6b86ec066056d679e41ad70c3a65a9450b';

/** 旧设备 Lite 顾客端登录页默认密码（6 位阿拉伯数字） */
export const DEFAULT_CUSTOMER_LITE_LOGIN_PASSWORD = '123456';

export const DEFAULT_SYSTEM_SETTINGS: SystemSettings = {
  maxItemsPerOrder: 5,
  lockMinutesBetweenOrders: 10,
  menuLunchIds: [],
  menuDinnerIds: [],
  menuSpecialIds: [],
  defaultUiLang: 'de',
  customerMealOverride: '',
  customerBackgroundPreset: 'black',
  adminAuthEnabled: false,
  adminAuthUsername: 'Admin',
  adminAuthPasswordHash: '',
  customerLiteLoginPassword: DEFAULT_CUSTOMER_LITE_LOGIN_PASSWORD,
  kitchenCategoryPrintMode: {},
};

/* ─────────── 全局 facade 局部断言 ─────────── */

interface RuntimeGlobal {
  RestaurantConfigLib?: RestaurantConfigLib;
  OS?: {
    registerModule?: (name: string, ref: unknown) => unknown;
  };
  localStorage: Storage;
  crypto?: Crypto;
  dispatchEvent(event: Event): boolean;
  addEventListener(type: 'storage', listener: (e: StorageEvent) => void): void;
}

const G = (typeof window !== 'undefined' ? window : globalThis) as unknown as RuntimeGlobal;

/* ─────────── normalize 系列 ─────────── */

const isLockMinutes = (n: number): n is LockMinutesBetweenOrders =>
  n === 0 || n === 10 || n === 15 || n === 20;

export const normalizeLockMinutesBetweenOrders = (raw: unknown): LockMinutesBetweenOrders => {
  const n = parseInt(String(raw ?? 0), 10);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (isLockMinutes(n)) return n;
  /** 非法非零值 → 取 10/15/20 中差值最小的（旧值兼容） */
  const candidates: LockMinutesBetweenOrders[] = [10, 15, 20];
  let best: LockMinutesBetweenOrders = 10;
  let bestDiff = Math.abs(n - 10);
  for (let i = 0; i < candidates.length; i++) {
    const d = Math.abs(n - candidates[i]);
    if (d < bestDiff) {
      bestDiff = d;
      best = candidates[i];
    }
  }
  return best;
};

const isCustomerBackgroundPreset = (v: string): v is CustomerBackgroundPreset =>
  (CUSTOMER_BACKGROUND_PRESET_ALLOWED as readonly string[]).includes(v);

export const normalizeCustomerBackgroundPreset = (raw: unknown): CustomerBackgroundPreset => {
  const v0 = raw != null ? String(raw).trim() : '';
  const v = LEGACY_CUSTOMER_BACKGROUND_PRESET[v0] ?? v0;
  return isCustomerBackgroundPreset(v) ? v : 'black';
};

export const normalizeAdminAuthPasswordHash = (raw: unknown): string => {
  const s = raw != null ? String(raw).trim().toLowerCase() : '';
  return /^[0-9a-f]{64}$/.test(s) ? s : '';
};

/** 仅接受 6 位纯数字；其它一律回退默认，避免 Lite 端无法解锁 */
export const normalizeCustomerLiteLoginPassword = (raw: unknown): string => {
  const s = raw != null ? String(raw).trim() : '';
  return /^\d{6}$/.test(s) ? s : DEFAULT_CUSTOMER_LITE_LOGIN_PASSWORD;
};

export const normalizeKitchenCategoryPrintMode = (raw: unknown): KitchenCategoryPrintModeMap => {
  const out: KitchenCategoryPrintModeMap = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  const rec = raw as Record<string, unknown>;
  for (const k of Object.keys(rec)) {
    const v = rec[k];
    if (v === 'single' || v === 'summary') out[k] = v;
  }
  return out;
};

export const normalizeMenuProfileIdArray = (raw: unknown): string[] => {
  if (!raw || !Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < raw.length; i++) {
    const id = String(raw[i]).trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
};

const isUiLang = (v: string): v is UiLangCode => (UI_LANG_ALLOWED as readonly string[]).includes(v);

const normalizeUiLang = (raw: unknown, fallback: UiLangCode = 'de'): UiLangCode => {
  if (raw == null) return fallback;
  const s = String(raw).trim();
  return s && isUiLang(s) ? s : fallback;
};

const normalizeMealOverride = (raw: unknown): MealOverride => {
  const s = raw != null ? String(raw).trim() : '';
  return s === 'lunch' || s === 'dinner' || s === 'special' ? s : '';
};

const clampMaxItems = (raw: unknown): number => {
  const n = parseInt(String(raw ?? 0), 10);
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n > 999) return 999;
  return n;
};

/* ─────────── 异步：管理员凭据哈希 ─────────── */

/**
 * SHA-256(ordersystem-admin:<username>:<password>) → hex
 * 局域网 http://192.168.x.x 等非安全上下文中 Chrome/Edge 不提供 crypto.subtle，须用纯 JS 回退（与 subtle 结果一致）。
 */
export const hashAdminAuthCredential = (username: unknown, password: unknown): Promise<string> => {
  const u = String(username ?? '').trim();
  const p = String(password ?? '');
  const payload = `ordersystem-admin:${u}:${p}`;
  const enc = new TextEncoder();
  const data = enc.encode(payload);
  const subtle = G.crypto?.subtle;
  if (subtle) {
    return subtle.digest('SHA-256', data).then((buf) =>
      Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join(''),
    );
  }
  return Promise.resolve(bytesToHex(sha256(data)));
};

/* ─────────── localStorage IO ─────────── */

const readJson = <T>(key: StorageKey, fallback: T): T | unknown => {
  try {
    const raw = G.localStorage.getItem(key);
    if (raw === null || raw === '') return fallback;
    try {
      return JSON.parse(raw) as unknown;
    } catch {
      return raw;
    }
  } catch (e) {
    console.warn('config-lib 读取失败:', e);
    return fallback;
  }
};

const writeJson = (key: StorageKey, value: unknown): void => {
  try {
    const str = typeof value === 'string' ? value : JSON.stringify(value);
    G.localStorage.setItem(key, str);
  } catch (e) {
    console.warn('config-lib 写入失败:', e);
  }
};

/* ─────────── 顶层副作用：跨标签 storage 同步 ─────────── */

const dispatchStorageSync = (key: StorageKey, eventName: string): void => {
  G.addEventListener('storage', (e) => {
    if (e.key !== key || e.newValue == null) return;
    let detail: unknown;
    try {
      detail = JSON.parse(e.newValue);
    } catch {
      detail = e.newValue;
    }
    G.dispatchEvent(new CustomEvent(eventName, { detail }));
  });
};

dispatchStorageSync(KEYS.RESTAURANT_INFO, 'restaurantInfoUpdated');
dispatchStorageSync(KEYS.SYSTEM_SETTINGS, 'restaurantSystemSettingsUpdated');
/** UI 语言：各页面对 KEYS.UI_LANG 已有 storage 监听，此处不再转发，避免重复刷新 */

/* ─────────── R/W：网络配置 ─────────── */

export const getNetworkConfig = (): NetworkConfig => {
  const d = readJson(KEYS.NETWORK, null);
  const obj: Record<string, unknown> = d && typeof d === 'object' ? (d as Record<string, unknown>) : {};
  return { apiBaseUrl: '', printProxyUrl: '', ...obj };
};

export const saveNetworkConfig = (partial: Partial<NetworkConfig>): NetworkConfig => {
  const next: NetworkConfig = { ...getNetworkConfig(), ...partial };
  writeJson(KEYS.NETWORK, next);
  return next;
};

/* ─────────── R/W：门店信息 ─────────── */

export const getRestaurantInfo = (): RestaurantInfo => {
  const d = readJson(KEYS.RESTAURANT_INFO, null);
  const obj: Record<string, unknown> = d && typeof d === 'object' ? (d as Record<string, unknown>) : {};
  return { ...DEFAULT_RESTAURANT_INFO, ...obj };
};

export const saveRestaurantInfo = (partial: Partial<RestaurantInfo>): RestaurantInfo => {
  const next: RestaurantInfo = { ...getRestaurantInfo(), ...partial };
  writeJson(KEYS.RESTAURANT_INFO, next);
  G.dispatchEvent(new CustomEvent('restaurantInfoUpdated', { detail: next }));
  return next;
};

/* ─────────── R/W：系统设置 ─────────── */

export const getSystemSettings = (): SystemSettings => {
  const d = readJson(KEYS.SYSTEM_SETTINGS, null);
  const o: Record<string, unknown> = d && typeof d === 'object' ? (d as Record<string, unknown>) : {};

  const maxItemsSrc = Object.prototype.hasOwnProperty.call(o, 'maxItemsPerOrder')
    ? o.maxItemsPerOrder
    : DEFAULT_SYSTEM_SETTINGS.maxItemsPerOrder;
  const maxItems = clampMaxItems(maxItemsSrc);

  const lockSrc = Object.prototype.hasOwnProperty.call(o, 'lockMinutesBetweenOrders')
    ? o.lockMinutesBetweenOrders
    : DEFAULT_SYSTEM_SETTINGS.lockMinutesBetweenOrders;
  const lockM = normalizeLockMinutesBetweenOrders(lockSrc);

  const defaultUiLang = normalizeUiLang(o.defaultUiLang, DEFAULT_SYSTEM_SETTINGS.defaultUiLang);
  const customerMealOverride = normalizeMealOverride(o.customerMealOverride);
  const customerBackgroundPreset = normalizeCustomerBackgroundPreset(o.customerBackgroundPreset);

  const adminAuthEnabled = o.adminAuthEnabled === true;
  const adminAuthUsername =
    o.adminAuthUsername != null && String(o.adminAuthUsername).trim()
      ? String(o.adminAuthUsername).trim()
      : DEFAULT_SYSTEM_SETTINGS.adminAuthUsername;
  let adminAuthPasswordHash = normalizeAdminAuthPasswordHash(o.adminAuthPasswordHash);
  if (adminAuthEnabled && !adminAuthPasswordHash) {
    adminAuthPasswordHash = DEFAULT_ADMIN_AUTH_PASSWORD_HASH;
  }
  const customerLiteLoginPassword = normalizeCustomerLiteLoginPassword(o.customerLiteLoginPassword);
  const kitchenCategoryPrintMode = normalizeKitchenCategoryPrintMode(o.kitchenCategoryPrintMode);

  return {
    maxItemsPerOrder: maxItems,
    lockMinutesBetweenOrders: lockM,
    menuLunchIds: normalizeMenuProfileIdArray(o.menuLunchIds),
    menuDinnerIds: normalizeMenuProfileIdArray(o.menuDinnerIds),
    menuSpecialIds: normalizeMenuProfileIdArray(o.menuSpecialIds),
    defaultUiLang,
    customerMealOverride,
    customerBackgroundPreset,
    adminAuthEnabled,
    adminAuthUsername,
    adminAuthPasswordHash,
    customerLiteLoginPassword,
    kitchenCategoryPrintMode,
  };
};

export const systemSettingsUseMealProfiles = (s: SystemSettings | null | undefined): boolean => {
  if (!s) return false;
  return (
    (s.menuLunchIds?.length ?? 0) +
      (s.menuDinnerIds?.length ?? 0) +
      (s.menuSpecialIds?.length ?? 0) >
    0
  );
};

export const saveSystemSettings = (partial: Partial<SystemSettings>): SystemSettings => {
  const cur = getSystemSettings();
  const p: Record<string, unknown> =
    partial && typeof partial === 'object' ? (partial as Record<string, unknown>) : {};
  const merged: Record<string, unknown> = { ...cur, ...p };

  const defaultUiLang = normalizeUiLang(merged.defaultUiLang, cur.defaultUiLang);
  const customerMealOverride = normalizeMealOverride(merged.customerMealOverride);
  const customerBackgroundPreset = normalizeCustomerBackgroundPreset(
    merged.customerBackgroundPreset != null ? merged.customerBackgroundPreset : cur.customerBackgroundPreset,
  );

  const adminAuthEnabled = merged.adminAuthEnabled === true;
  const adminAuthUsername =
    merged.adminAuthUsername != null && String(merged.adminAuthUsername).trim()
      ? String(merged.adminAuthUsername).trim()
      : cur.adminAuthUsername || DEFAULT_SYSTEM_SETTINGS.adminAuthUsername;
  let adminAuthPasswordHash = normalizeAdminAuthPasswordHash(
    merged.adminAuthPasswordHash != null ? merged.adminAuthPasswordHash : cur.adminAuthPasswordHash,
  );
  if (adminAuthEnabled && !adminAuthPasswordHash) {
    adminAuthPasswordHash = DEFAULT_ADMIN_AUTH_PASSWORD_HASH;
  }
  const customerLiteLoginPassword = normalizeCustomerLiteLoginPassword(
    merged.customerLiteLoginPassword != null ? merged.customerLiteLoginPassword : cur.customerLiteLoginPassword,
  );
  const kitchenCategoryPrintMode = normalizeKitchenCategoryPrintMode(
    merged.kitchenCategoryPrintMode != null ? merged.kitchenCategoryPrintMode : cur.kitchenCategoryPrintMode,
  );

  const next: SystemSettings = {
    maxItemsPerOrder: clampMaxItems(merged.maxItemsPerOrder),
    lockMinutesBetweenOrders: normalizeLockMinutesBetweenOrders(merged.lockMinutesBetweenOrders ?? 0),
    menuLunchIds: normalizeMenuProfileIdArray(merged.menuLunchIds),
    menuDinnerIds: normalizeMenuProfileIdArray(merged.menuDinnerIds),
    menuSpecialIds: normalizeMenuProfileIdArray(merged.menuSpecialIds),
    defaultUiLang,
    customerMealOverride,
    customerBackgroundPreset,
    adminAuthEnabled,
    adminAuthUsername,
    adminAuthPasswordHash,
    customerLiteLoginPassword,
    kitchenCategoryPrintMode,
  };

  writeJson(KEYS.SYSTEM_SETTINGS, next);
  G.dispatchEvent(new CustomEvent('restaurantSystemSettingsUpdated', { detail: next }));
  return next;
};

/* ─────────── R/W：UI 语言 ─────────── */

export const getUiLang = (): UiLangCode => {
  const v = readJson(KEYS.UI_LANG, null);
  const s = typeof v === 'string' ? v.trim() : '';
  if (s && isUiLang(s)) return s;
  /** 无显式选择 → 取系统默认 */
  const fallback = getSystemSettings().defaultUiLang || 'de';
  return isUiLang(fallback) ? fallback : 'de';
};

export const setUiLang = (code: string): void => {
  if (!isUiLang(code)) return;
  writeJson(KEYS.UI_LANG, code);
  G.dispatchEvent(new CustomEvent('restaurantUiLangUpdated', { detail: code }));
};

/* ─────────── 暴露 ─────────── */

export interface RestaurantConfigLib {
  KEYS: typeof KEYS;
  UI_LANG_ALLOWED: typeof UI_LANG_ALLOWED;
  CUSTOMER_BACKGROUND_PRESET_ALLOWED: typeof CUSTOMER_BACKGROUND_PRESET_ALLOWED;
  ORDER_LOCK_MINUTES_STOPS: typeof ORDER_LOCK_MINUTES_STOPS;
  DEFAULT_RESTAURANT_INFO: RestaurantInfo;
  DEFAULT_SYSTEM_SETTINGS: SystemSettings;
  DEFAULT_ADMIN_AUTH_PASSWORD_HASH: string;
  DEFAULT_CUSTOMER_LITE_LOGIN_PASSWORD: string;
  hashAdminAuthCredential(username: unknown, password: unknown): Promise<string>;
  getNetworkConfig(): NetworkConfig;
  saveNetworkConfig(partial: Partial<NetworkConfig>): NetworkConfig;
  getRestaurantInfo(): RestaurantInfo;
  saveRestaurantInfo(partial: Partial<RestaurantInfo>): RestaurantInfo;
  getSystemSettings(): SystemSettings;
  saveSystemSettings(partial: Partial<SystemSettings>): SystemSettings;
  systemSettingsUseMealProfiles(s: SystemSettings | null | undefined): boolean;
  normalizeKitchenCategoryPrintMode(raw: unknown): KitchenCategoryPrintModeMap;
  normalizeMenuProfileIdArray(raw: unknown): string[];
  getUiLang(): UiLangCode;
  setUiLang(code: string): void;
}

const lib: RestaurantConfigLib = {
  KEYS,
  UI_LANG_ALLOWED,
  CUSTOMER_BACKGROUND_PRESET_ALLOWED,
  ORDER_LOCK_MINUTES_STOPS,
  DEFAULT_RESTAURANT_INFO,
  DEFAULT_SYSTEM_SETTINGS,
  DEFAULT_ADMIN_AUTH_PASSWORD_HASH,
  DEFAULT_CUSTOMER_LITE_LOGIN_PASSWORD,
  hashAdminAuthCredential,
  getNetworkConfig,
  saveNetworkConfig,
  getRestaurantInfo,
  saveRestaurantInfo,
  getSystemSettings,
  saveSystemSettings,
  systemSettingsUseMealProfiles,
  normalizeKitchenCategoryPrintMode,
  normalizeMenuProfileIdArray,
  getUiLang,
  setUiLang,
};

G.RestaurantConfigLib = lib;

if (typeof G.OS?.registerModule === 'function') {
  try {
    G.OS.registerModule('configLib', lib);
  } catch {
    /* OS facade 注册失败不致命 */
  }
}
